
import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, MapPin } from 'lucide-react';

export interface LocationResult {
  address: string;
  lat: number;
  lng: number;
  city: string;
  country: string;
  timezone: string;
}

interface LocationAutocompleteProps {
  value: string;
  onChange: (result: LocationResult | null, rawText: string) => void;
  placeholder?: string;
}

const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({ value, onChange, placeholder }) => {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);
  const debounceRef = useRef<NodeJS.Timeout>();
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync external value
  useEffect(() => { setQuery(value); }, [value]);

  // Fetch mapbox token once
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-mapbox-token');
        if (!error && data?.mapboxToken) {
          setMapboxToken(data.mapboxToken);
        }
      } catch { /* fallback to manual input */ }
    };
    fetchToken();
  }, []);

  // Click outside to close
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const searchPlaces = async (q: string) => {
    if (!mapboxToken || q.length < 3) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${mapboxToken}&types=address,place,poi&limit=5`
      );
      const data = await res.json();
      setSuggestions(data.features || []);
      setShowDropdown(true);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (text: string) => {
    setQuery(text);
    onChange(null, text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchPlaces(text), 400);
  };

  const extractContext = (feature: any) => {
    let city = '';
    let country = '';
    const ctx = feature.context || [];
    for (const c of ctx) {
      if (c.id?.startsWith('place')) city = c.text;
      if (c.id?.startsWith('country')) country = c.text;
    }
    // If feature itself is a place
    if (!city && feature.place_type?.includes('place')) city = feature.text;
    return { city, country };
  };

  const handleSelect = (feature: any) => {
    const [lng, lat] = feature.center;
    const { city, country } = extractContext(feature);
    const address = feature.place_name || feature.text;
    // Approximate timezone from longitude
    const offsetHours = Math.round(lng / 15);
    const tzGuess = Intl.DateTimeFormat().resolvedOptions().timeZone; // Fallback to browser tz
    
    setQuery(address);
    setShowDropdown(false);
    setSuggestions([]);
    onChange({ address, lat, lng, city, country, timezone: tzGuess }, address);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={e => handleInputChange(e.target.value)}
          placeholder={placeholder || 'Search address...'}
          className="pl-9"
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-48 overflow-auto">
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
              onClick={() => handleSelect(s)}
            >
              <span className="font-medium">{s.text}</span>
              {s.place_name !== s.text && (
                <span className="text-muted-foreground ml-1 text-xs">{s.place_name}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;
